import type { PlannedExpenseLineInput } from '@/features/cases/api/useCase'
import type { ExpenseLine } from '@/types/domain'

/* Which generalized plan line a direction's per-unit rates DETAIL (spec
 * 2026-09-01-expense-detail-grouping, L7/L8; UX walkthrough T-1/T-2). The
 * name is S-12's: the rule is the same for every unit kind since S-18b — a
 * container's or a B/L's rate details the quote's carriage line exactly as a
 * truck's does.
 *
 * The quote carries the movement once — «Transportation service 8 709» — and
 * the unit cards write it again per unit. The link between the two is
 * `detailOf` on each per-unit cost line; without it the case plans the same
 * freight twice and shows a loss that does not exist.
 *
 * This file is the derivation the select is built from, kept out of the
 * component so its four answers can be pinned one by one. Both walkthrough
 * bugs lived here:
 *
 *  - an untouched select on a case with two generalized lines answered
 *    «не детализирует» on the operator's behalf, so the rates were saved
 *    unlinked. An untouched select now answers NOTHING (`UNSET_PARENT`), and a
 *    save that writes new rates is refused until somebody answers;
 *  - a direction whose rates were already unlinked took the single candidate
 *    as its default, which made the choice "moved" on first paint and re-filed
 *    the rates on the next unrelated save. A direction with plans now defaults
 *    to its OWN answer, and only a direction with none gets the single-candidate
 *    default.
 */

/** The untouched, unanswered select. Not among the options — so `Select`
 *  shows its placeholder — and never sent: neither a `detailOf` nor a PATCH is
 *  composed from it. `''` stays the explicit «не детализирует».
 *
 *  Since S-12 it is also what plans with neither a parent nor a stored
 *  `standsAlone` derive: `detail_of NULL` alone used to mean both "stands for
 *  itself" and "never answered", and the server could not tell them apart. */
export const UNSET_PARENT = '__unset__'

/** Whether a select value names a plan line, as opposed to «не детализирует»
 *  or no answer at all. The one test every write path asks before linking. */
export function isParentLine(value: string): boolean {
  return value !== '' && value !== UNSET_PARENT
}

export interface TruckDetail {
  /** Lines that may stand as a parent: a top-level unattributed plan of this
   *  case (spec L3) — the same facts the server checks before it accepts the
   *  link, so the select cannot offer a choice the save would refuse. */
  candidates: ExpenseLine[]
  /** This direction's own plans — per unit, and the leg rate (S-18a) —
   *  which carry its current answer. */
  legPlans: ExpenseLine[]
  /** The answer the plans agree on: a parent's id, `''` when every one of
   *  them carries the stored «не детализирует» (`standsAlone`), and
   *  `UNSET_PARENT` when they are unanswered or disagree — rows filed under two
   *  answers are a state no select can represent. */
  currentParent: string
  /** The plans carry more than one answer. A plan standing alone is an answer
   *  distinct from any parent and from no answer at all. */
  mixed: boolean
  /** The select's value: the explicit choice, else the default. */
  chosenParent: string
  /** What new rates are linked to, `null` for no link. */
  linkTo: string | null
  /** The chosen answer is «не детализирует»: new rates are created with
   *  `standsAlone: true`, and a move PATCHes `{detailOf: null, standsAlone:
   *  true}`. False for a parent AND for the unanswered select, whose sentinel
   *  never reaches the wire. */
  standsAlone: boolean
  /** The choice differs from what the plans already say — or the plans
   *  disagree and any answer settles them — so saving re-files them (L8).
   *  Only ever true after an explicit choice. */
  parentMoved: boolean
  /** New rates are about to be written and nobody has said what they detail. */
  mustChoose: boolean
  /** Candidates no line details. A candidate another direction's trucks
   *  already detail is out of the served totals and cannot be counted twice. */
  unlinkedCandidates: ExpenseLine[]
  /** This direction has UNANSWERED per-truck plans while such a candidate
   *  exists — the double count the walkthrough found, said out loud. A plan
   *  standing alone is an answer and never warns. */
  warn: boolean
}

export function truckDetail({
  lines,
  legId,
  parentChoice,
  hasNewRates,
}: {
  lines: readonly ExpenseLine[]
  legId: string
  /** `null` while the select is untouched. */
  parentChoice: string | null
  /** A draft row carries a number and a well-formed rate — computed from the
   *  rows being typed, because the saved lines arrive only after the save. */
  hasNewRates: boolean
}): TruckDetail {
  const candidates = lines.filter(
    (line) => line.legId === undefined && line.detailOf === undefined && !line.unplanned,
  )
  /* An unplanned actual on a unit — a payment for a unit with no rate —
     carries the leg and the unit too, but it is no plan: it cannot carry
     `detailOf` (spec L2) and the server refuses to PATCH it.

     A plan with the leg and NO truck — the leg rate of «Плечо целиком» — is
     one of the leg's plans since S-18a (owner: the leg rate is a truck rate
     for S-12): the server's double count, `stands_alone` CHECK and
     `StandsAloneAllowed` dropped the unit condition with it. */
  const legPlans = lines.filter((line) => line.legId === legId && !line.unplanned)
  const answers = legPlans.map(answerOf)
  const mixed = answers.some((answer) => answer !== answers[0])
  const currentParent =
    answers.length > 0 && !mixed ? (answers[0] ?? UNSET_PARENT) : UNSET_PARENT

  /* Derived rather than seeded into state: the list arrives after the first
     paint, and a state seeded from an empty list would hold its answer for the
     rest of the visit. */
  /* Plans filed under two answers have no one answer, and unanswered plans
     have none either: `''` would show «не детализирует» on a select nobody
     touched — `currentParent` is already UNSET in both. */
  const defaultParent =
    legPlans.length > 0
      ? currentParent
      : candidates.length === 1
        ? candidates[0]!.id
        : UNSET_PARENT
  const chosenParent = parentChoice ?? defaultParent

  const named = new Set(lines.flatMap((line) => (line.detailOf ? [line.detailOf] : [])))
  const unlinkedCandidates = candidates.filter((line) => !named.has(line.id))

  return {
    candidates,
    legPlans,
    currentParent,
    mixed,
    chosenParent,
    linkTo: isParentLine(chosenParent) ? chosenParent : null,
    standsAlone: chosenParent === '',
    /* A move from UNSET to `''` counts: it is how a direction answered before
       S-12 (or never) gets its «не детализирует» stored. */
    parentMoved:
      legPlans.length > 0 &&
      chosenParent !== UNSET_PARENT &&
      (mixed || chosenParent !== currentParent),
    /* Only while the DEFAULT is unanswered: a stored answer — a parent or a
       stored «не детализирует» — is not asked again on the next truck. */
    mustChoose:
      hasNewRates &&
      parentChoice === null &&
      defaultParent === UNSET_PARENT &&
      candidates.length > 0,
    unlinkedCandidates,
    warn:
      legPlans.some((line) => answerOf(line) === UNSET_PARENT) &&
      unlinkedCandidates.length > 0,
  }
}

/** The answer the rate lines a failed press holds were composed with — read
 *  off the lines, never off the select, because «Повторить» re-sends them as
 *  they are: a parent's id, `''` for `standsAlone`, `UNSET_PARENT` for
 *  neither; `undefined` when no line is held. One press composes every line
 *  with one answer, so the first line speaks for all. */
export function heldParent(
  lines: ReadonlyArray<Pick<PlannedExpenseLineInput, 'detailOf' | 'standsAlone'>> | undefined,
): string | undefined {
  const first = lines?.[0]
  if (!first) return undefined
  return first.detailOf ?? (first.standsAlone ? '' : UNSET_PARENT)
}

/** One per-truck plan's own answer: its parent, `''` for a stored «не
 *  детализирует», `UNSET_PARENT` for none. */
function answerOf(line: ExpenseLine): string {
  return line.detailOf ?? (line.standsAlone ? '' : UNSET_PARENT)
}
